import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { UtensilsCrossed, Soup, ChefHat, Sandwich, Salad, Wine } from 'lucide-react'

const categories = [
  {
    key: 'antipasti',
    icon: UtensilsCrossed,
    image: '/images/categoria_aperitivo-1.jpg',
    color: 'from-orange-500/20 to-amber-600/20',
  },
  {
    key: 'primi',
    icon: Soup,
    image: '/images/categoria_primi-piatti.jpg',
    color: 'from-red-500/20 to-rose-600/20',
  },
  {
    key: 'secondi',
    icon: ChefHat,
    image: '/images/Ricette_652X732-Fiordicotto.jpg',
    color: 'from-amber-500/20 to-orange-600/20',
  },
  {
    key: 'panini',
    icon: Sandwich,
    image: '/images/categoria_panini.jpg',
    color: 'from-yellow-500/20 to-amber-600/20',
  },
  {
    key: 'insalate',
    icon: Salad,
    image: '/images/categoria_insalate.jpg',
    color: 'from-green-500/20 to-emerald-600/20',
  },
  {
    key: 'aperitivo',
    icon: Wine,
    image: '/images/categoria_aperitivo-1.jpg',
    color: 'from-purple-500/20 to-violet-600/20',
  },
]

export function CategoriesGrid() {
  const { t } = useTranslation('common')

  return (
    <section className="section-padding bg-[#FDF8F3] relative" aria-labelledby="categories-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#5C1A1A]/10 text-[#5C1A1A] text-sm font-semibold rounded-full mb-4">
            {t('nav.recipes')}
          </span>
          <h2 id="categories-title" className="heading-section text-[#4E2118] mb-4">
            {t('recipes.categories.title')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70 max-w-2xl mx-auto">
            {t('recipes.categories.subtitle')}
          </p>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            const Icon = category.icon
            return (
              <motion.div
                key={category.key}
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <article className="relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer h-full">
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={category.image}
                      alt={t(`recipes.categories.${category.key}.name`)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-60`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/80 via-[#4E2118]/30 to-transparent" />

                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-[#4E2118]" />
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-serif text-2xl font-bold text-white mb-1">
                        {t(`recipes.categories.${category.key}.name`)}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {t(`recipes.categories.${category.key}.recipeCount`)}
                      </p>
                    </div>
                  </div>

                  {/* Bottom Section */}
                  <div className="p-5 bg-white">
                    <p className="text-[#4E2118]/70 text-sm leading-relaxed">
                      {t(`recipes.categories.${category.key}.description`)}
                    </p>
                    <div className="mt-4 flex items-center text-[#DF631E] font-semibold text-sm group-hover:gap-2 transition-all">
                      <span>{t('buttons.discover')}</span>
                      <svg
                        className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </article>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
